import clsx from "clsx";
import { useCallback } from "react";

import {
  DEFAULT_BLUR,
  DEFAULT_DROP_SHADOW,
  DEFAULT_INNER_SHADOW,
  DEFAULT_NOISE,
  DEFAULT_TEXTURE,
  TEXTURE_BLEND_MODES,
  TEXTURE_PATTERNS,
  renderTexturePattern,
} from "@excalidraw/element";

import type {
  ElementBlur,
  ElementEffects,
  ElementNoise,
  ElementShadow,
  ElementTexture,
  ElementTextureFit,
  FileId,
} from "@excalidraw/element/types";

import type { TexturePatternId } from "@excalidraw/element/texturePatterns";

import { t } from "../i18n";

import { MinusIcon, eyeIcon, eyeClosedIcon } from "./icons";

import "./EffectsPanel.scss";

import type { ReactNode } from "react";

export type EffectsChange = {
  key: keyof ElementEffects;
  value: ElementShadow | ElementBlur | ElementNoise | ElementTexture | null;
};

type EffectValue = ElementShadow | ElementBlur | ElementNoise | ElementTexture;

type EffectMeta = {
  key: keyof ElementEffects;
  label: string;
  icon: ReactNode;
  defaults: EffectValue;
};

// ---- Abschnitt 20: small identifying glyphs for the effects list — no
// icon set for these existed anywhere in the codebase (checked icons.tsx),
// so these are new, minimal, matching the existing OpacityGlyph/
// DurationGlyph pattern (DesignPanel/index.tsx): 20x20 viewBox, stroke =
// currentColor, no hardcoded color of their own.
const DropShadowGlyph = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="4" y="4" width="10" height="10" rx="2" opacity="0.45" />
    <rect x="7" y="7" width="10" height="10" rx="2" />
  </svg>
);

const InnerShadowGlyph = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="4" y="4" width="12" height="12" rx="2" />
    <path d="M13.5 6.2a6 6 0 0 1 0 7.6" opacity="0.55" strokeLinecap="round" />
  </svg>
);

const LayerBlurGlyph = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="10" cy="10" r="3" />
    <circle cx="10" cy="10" r="6" opacity="0.55" />
    <circle cx="10" cy="10" r="8.5" opacity="0.3" />
  </svg>
);

const BackgroundBlurGlyph = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3.5" y="3.5" width="13" height="13" rx="2" />
    <path
      d="M6.5 8h7M6.5 10.5h7M6.5 13h4.5"
      opacity="0.55"
      strokeLinecap="round"
    />
  </svg>
);

const NoiseGlyph = (
  <svg viewBox="0 0 20 20" fill="currentColor" stroke="none">
    <circle cx="6" cy="6" r="1.1" />
    <circle cx="11.5" cy="5" r="1.1" />
    <circle cx="15" cy="8.5" r="1.1" />
    <circle cx="5" cy="11" r="1.1" />
    <circle cx="9.5" cy="10" r="1.1" />
    <circle cx="14" cy="13.5" r="1.1" />
    <circle cx="7" cy="15" r="1.1" />
    <circle cx="12.5" cy="16" r="1.1" />
  </svg>
);

const TextureGlyph = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3.5" y="3.5" width="13" height="13" rx="2" />
    <path d="M3.5 10h13M10 3.5v13" />
  </svg>
);

const splitHex = (color: string) => {
  const rgb = /^#[0-9a-fA-F]{6}/.test(color) ? color.slice(0, 7) : "#000000";
  const alpha = /^#[0-9a-fA-F]{8}$/.test(color)
    ? parseInt(color.slice(7, 9), 16)
    : 255;
  return { rgb, alpha };
};

const joinHex = (rgb: string, alpha: number) =>
  alpha >= 255
    ? rgb
    : `${rgb}${Math.max(0, Math.min(255, Math.round(alpha)))
        .toString(16)
        .padStart(2, "0")}`;

const NumberInput = ({
  label,
  value,
  min,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  onChange: (value: number) => void;
}) => (
  <label className="effects-panel__number">
    <span>{label}</span>
    <input
      type="number"
      value={value}
      min={min}
      onChange={(event) => {
        const next = event.target.valueAsNumber;
        if (Number.isFinite(next)) {
          onChange(min != null ? Math.max(min, next) : next);
        }
      }}
    />
  </label>
);

const ShadowControls = ({
  shadow,
  onChange,
}: {
  shadow: ElementShadow;
  onChange: (shadow: ElementShadow) => void;
}) => {
  const { rgb, alpha } = splitHex(shadow.color);
  return (
    <div className="effects-panel__controls">
      <label className="effects-panel__color">
        <input
          type="color"
          value={rgb}
          onChange={(event) =>
            onChange({ ...shadow, color: joinHex(event.target.value, alpha) })
          }
        />
        <input
          type="range"
          min={0}
          max={255}
          value={alpha}
          title={t("labels.effects.alpha")}
          onChange={(event) =>
            onChange({ ...shadow, color: joinHex(rgb, +event.target.value) })
          }
        />
      </label>
      <div className="effects-panel__numbers">
        <NumberInput
          label="X"
          value={shadow.offsetX}
          onChange={(offsetX) => onChange({ ...shadow, offsetX })}
        />
        <NumberInput
          label="Y"
          value={shadow.offsetY}
          onChange={(offsetY) => onChange({ ...shadow, offsetY })}
        />
        <NumberInput
          label={t("labels.effects.blur")}
          value={shadow.blur}
          min={0}
          onChange={(blur) => onChange({ ...shadow, blur })}
        />
        <NumberInput
          label={t("labels.effects.spread")}
          value={shadow.spread}
          min={0}
          onChange={(spread) => onChange({ ...shadow, spread })}
        />
      </div>
    </div>
  );
};

const BlurControls = ({
  blur,
  onChange,
}: {
  blur: ElementBlur;
  onChange: (blur: ElementBlur) => void;
}) => (
  <div className="effects-panel__controls">
    <label className="effects-panel__number effects-panel__radius">
      <span>{t("labels.effects.radius")}</span>
      <input
        type="range"
        min={0}
        max={100}
        value={blur.radius}
        onChange={(event) => onChange({ ...blur, radius: +event.target.value })}
      />
    </label>
  </div>
);

const NoiseControls = ({
  noise,
  onChange,
}: {
  noise: ElementNoise;
  onChange: (noise: ElementNoise) => void;
}) => (
  <div className="effects-panel__controls">
    <label className="effects-panel__number effects-panel__radius">
      <span>{t("labels.effects.intensity")}</span>
      <input
        type="range"
        min={0}
        max={100}
        value={noise.intensity}
        onChange={(event) =>
          onChange({ ...noise, intensity: +event.target.value })
        }
      />
    </label>
    <NumberInput
      label={t("labels.effects.grainSize")}
      value={noise.size}
      min={1}
      onChange={(size) => onChange({ ...noise, size })}
    />
  </div>
);

/** Swatch drawn with the same code the renderer uses, so what you pick is
 *  exactly what lands on the shape. Painted over white because the ink is
 *  transparent — on the canvas the element's own fill plays that role. */
const PatternSwatch = ({
  pattern,
  color,
  selected,
  onSelect,
}: {
  pattern: { id: TexturePatternId; label: string };
  color: string;
  selected: boolean;
  onSelect: () => void;
}) => {
  const paint = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) {
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      renderTexturePattern(ctx, canvas.width, pattern.id, color);
    },
    [pattern.id, color],
  );

  return (
    <button
      type="button"
      className={clsx("effects-panel__swatch", {
        "effects-panel__swatch--selected": selected,
      })}
      onClick={onSelect}
      title={pattern.label}
      aria-label={pattern.label}
      aria-pressed={selected}
    >
      <canvas ref={paint} width={72} height={72} />
    </button>
  );
};

const TEXTURE_FIT_OPTIONS: {
  value: ElementTextureFit;
  labelKey: Parameters<typeof t>[0];
}[] = [
  { value: "tile", labelKey: "labels.effects.tile" },
  { value: "stretch", labelKey: "labels.effects.stretch" },
  { value: "fill", labelKey: "labels.effects.fill" },
  { value: "fit", labelKey: "labels.effects.fit" },
];

const TextureControls = ({
  texture,
  onChange,
  onPickTexture,
}: {
  texture: ElementTexture;
  onChange: (texture: ElementTexture) => void;
  onPickTexture: () => Promise<FileId | null>;
}) => (
  <div className="effects-panel__controls">
    <div className="effects-panel__swatches">
      {TEXTURE_PATTERNS.map((pattern) => (
        <PatternSwatch
          key={pattern.id}
          pattern={pattern}
          color={texture.color}
          selected={!texture.fileId && texture.pattern === pattern.id}
          // picking a built-in drops the uploaded image — one source at a
          // time, otherwise the file would silently keep winning
          onSelect={() =>
            onChange({
              ...texture,
              pattern: pattern.id,
              fileId: null,
              enabled: true,
            })
          }
        />
      ))}
    </div>
    <button
      type="button"
      className="effects-panel__upload"
      onClick={async () => {
        const fileId = await onPickTexture();
        if (fileId) {
          onChange({ ...texture, fileId, pattern: null, enabled: true });
        }
      }}
    >
      {texture.fileId
        ? t("labels.effects.changeImage")
        : t("labels.effects.uploadImage")}
    </button>
    {!texture.fileId && (
      <label className="effects-panel__color">
        <input
          type="color"
          value={
            /^#[0-9a-fA-F]{6}$/.test(texture.color) ? texture.color : "#000000"
          }
          title={t("labels.effects.tint")}
          onChange={(event) =>
            onChange({ ...texture, color: event.target.value })
          }
        />
        <span className="effects-panel__color-label">
          {t("labels.effects.tint")}
        </span>
      </label>
    )}
    <div className="effects-panel__fit">
      {TEXTURE_FIT_OPTIONS.map((option) => (
        <label key={option.value}>
          <input
            type="radio"
            checked={texture.fit === option.value}
            onChange={() => onChange({ ...texture, fit: option.value })}
          />
          <span>{t(option.labelKey)}</span>
        </label>
      ))}
    </div>
    <div className="effects-panel__numbers">
      <NumberInput
        label={t("labels.effects.scale")}
        value={texture.scale}
        min={0.01}
        onChange={(scale) => onChange({ ...texture, scale })}
      />
      <NumberInput
        label={t("labels.effects.rotation")}
        value={texture.rotation}
        onChange={(rotation) => onChange({ ...texture, rotation })}
      />
      <NumberInput
        label="X"
        value={texture.offsetX}
        onChange={(offsetX) => onChange({ ...texture, offsetX })}
      />
      <NumberInput
        label="Y"
        value={texture.offsetY}
        onChange={(offsetY) => onChange({ ...texture, offsetY })}
      />
    </div>
    <label className="effects-panel__number">
      <span>{t("labels.effects.blendMode")}</span>
      <select
        className="design-panel__select"
        value={texture.blendMode}
        onChange={(event) =>
          onChange({
            ...texture,
            blendMode: event.target.value as ElementTexture["blendMode"],
          })
        }
      >
        {TEXTURE_BLEND_MODES.map((mode) => (
          <option key={mode} value={mode}>
            {mode}
          </option>
        ))}
      </select>
    </label>
    <label className="effects-panel__number effects-panel__radius">
      <span>{t("labels.effects.opacity")}</span>
      <input
        type="range"
        min={0}
        max={100}
        value={texture.opacity}
        onChange={(event) =>
          onChange({ ...texture, opacity: +event.target.value })
        }
      />
    </label>
  </div>
);

// ---- Abschnitt 20: dropdown field listing only the effect types not yet
// added. Same native `<select class="design-panel__select">` as the Screen
// size preset (DesignPanel/index.tsx) so both read as one control — it stays
// on the empty placeholder option, since picking an entry adds it and it
// then disappears from `available`.
const AddEffectSelect = ({
  available,
  onPick,
}: {
  available: EffectMeta[];
  onPick: (meta: EffectMeta) => void;
}) => {
  const allAdded = available.length === 0;

  return (
    <select
      className="design-panel__select"
      title="Add effect"
      aria-label="Add effect"
      value=""
      disabled={allAdded}
      onChange={(event) => {
        const meta = available.find((item) => item.key === event.target.value);
        if (meta) {
          onPick(meta);
        }
      }}
    >
      <option value="">{allAdded ? "All effects added" : "Add effect…"}</option>
      {available.map((meta) => (
        <option key={meta.key} value={meta.key}>
          {meta.label}
        </option>
      ))}
    </select>
  );
};

export const EffectsPanel = ({
  effects,
  onChange,
  onPickTexture,
  backgroundBlurObscured = false,
}: {
  effects: ElementEffects | null;
  onChange: (change: EffectsChange) => void;
  onPickTexture: () => Promise<FileId | null>;
  /** the element's fully opaque fill would hide the backdrop blur — show a hint */
  backgroundBlurObscured?: boolean;
}) => {
  // recomputed every render (like the old shadowRow/blurRow closures) so
  // labels stay correct across a runtime language switch — t() must not be
  // called at module scope.
  const effectMeta: EffectMeta[] = [
    {
      key: "dropShadow",
      label: t("labels.effects.dropShadow"),
      icon: DropShadowGlyph,
      defaults: DEFAULT_DROP_SHADOW,
    },
    {
      key: "innerShadow",
      label: t("labels.effects.innerShadow"),
      icon: InnerShadowGlyph,
      defaults: DEFAULT_INNER_SHADOW,
    },
    {
      key: "layerBlur",
      label: t("labels.effects.layerBlur"),
      icon: LayerBlurGlyph,
      defaults: DEFAULT_BLUR,
    },
    {
      key: "backgroundBlur",
      label: t("labels.effects.backgroundBlur"),
      icon: BackgroundBlurGlyph,
      defaults: DEFAULT_BLUR,
    },
    {
      key: "noise",
      label: t("labels.effects.noise"),
      icon: NoiseGlyph,
      defaults: DEFAULT_NOISE,
    },
    {
      key: "texture",
      label: t("labels.effects.texture"),
      icon: TextureGlyph,
      defaults: DEFAULT_TEXTURE,
    },
  ];

  // "active" = actually added (present in the data), regardless of its own
  // enabled/hidden state — matches the spec's "nur eine Liste der
  // tatsächlich hinzugefügten Effekte".
  const activeMeta = effectMeta.filter((meta) => !!effects?.[meta.key]);
  const availableMeta = effectMeta.filter((meta) => !effects?.[meta.key]);

  // Plus icon: adds the effect (creates the entry, enabled by default) —
  // reuses the exact same defaults the old checkbox's onToggle(true) did.
  const addEffect = (meta: EffectMeta) =>
    onChange({ key: meta.key, value: { ...meta.defaults, enabled: true } });

  // Minus icon: removes the entry entirely (data goes back to undefined via
  // normalizeEffects) — it disappears from the active list.
  const removeEffect = (key: keyof ElementEffects) =>
    onChange({ key, value: null });

  // Eye icon: flips `enabled` while keeping the rest of the effect's config
  // — this is the exact same flag applyEffectsToCanvas/renderElement.ts
  // already gate rendering on (packages/element/src/effects.ts), so this
  // is a real visibility toggle, not a cosmetic one. The entry stays in the
  // list either way.
  const toggleVisibility = (key: keyof ElementEffects, current: EffectValue) =>
    onChange({ key, value: { ...current, enabled: !current.enabled } });

  return (
    <div className="effects-panel">
      <div className="design-panel__section-heading">
        <span>{t("labels.effects.title")}</span>
      </div>
      <AddEffectSelect available={availableMeta} onPick={addEffect} />
      {activeMeta.length === 0 ? (
        <div className="effects-panel__empty">No effects added</div>
      ) : (
        <div className="effects-panel__list">
          {activeMeta.map((meta) => {
            const value = effects![meta.key]!;
            return (
              <div
                key={meta.key}
                className={clsx("effects-panel__item", {
                  "effects-panel__item--hidden": !value.enabled,
                })}
              >
                <div className="effects-panel__item-header">
                  <span className="effects-panel__row-icon-glyph">
                    {meta.icon}
                  </span>
                  <span className="effects-panel__item-label">
                    {meta.label}
                  </span>
                  <button
                    type="button"
                    className="design-panel__row-icon"
                    onClick={() => toggleVisibility(meta.key, value)}
                    title={value.enabled ? "Hide effect" : "Show effect"}
                    aria-label={value.enabled ? "Hide effect" : "Show effect"}
                    aria-pressed={!value.enabled}
                  >
                    {value.enabled ? eyeIcon : eyeClosedIcon}
                  </button>
                  <button
                    type="button"
                    className="design-panel__row-icon"
                    onClick={() => removeEffect(meta.key)}
                    title="Remove effect"
                    aria-label="Remove effect"
                  >
                    {MinusIcon}
                  </button>
                </div>
                <div className="effects-panel__item-controls">
                  {(meta.key === "dropShadow" ||
                    meta.key === "innerShadow") && (
                    <ShadowControls
                      shadow={value as ElementShadow}
                      onChange={(next) =>
                        onChange({ key: meta.key, value: next })
                      }
                    />
                  )}
                  {(meta.key === "layerBlur" ||
                    meta.key === "backgroundBlur") && (
                    <>
                      <BlurControls
                        blur={value as ElementBlur}
                        onChange={(next) =>
                          onChange({ key: meta.key, value: next })
                        }
                      />
                      {meta.key === "backgroundBlur" &&
                        backgroundBlurObscured && (
                          <div className="effects-panel__hint">
                            {t("labels.effects.backgroundBlurHint")}
                          </div>
                        )}
                    </>
                  )}
                  {meta.key === "noise" && (
                    <NoiseControls
                      noise={value as ElementNoise}
                      onChange={(next) =>
                        onChange({ key: meta.key, value: next })
                      }
                    />
                  )}
                  {meta.key === "texture" && (
                    <TextureControls
                      texture={value as ElementTexture}
                      onChange={(next) =>
                        onChange({ key: meta.key, value: next })
                      }
                      onPickTexture={onPickTexture}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
